import { cva, type VariantProps } from "class-variance-authority";
import { Slot as SlotPrimitive } from "radix-ui";
import type { ComponentProps } from "react";

import { cn } from "../lib/utils";
import { Spinner } from "./spinner";

const buttonVariants = cva(
  "ausd-text-base-medium inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-1 focus-visible:ring-ring aria-invalid:ring-destructive/20 aria-invalid:border-destructive",
  {
    defaultVariants: {
      size: "default",
      variant: "default",
    },
    variants: {
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        icon: "size-9",
        "icon-lg": "size-9 [&_svg]:size-6",
        "icon-sm": "size-6 [&_svg]:size-4",
        lg: "h-10 rounded-md px-8 has-[>svg]:px-4",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5 text-xs",
        wide: "h-9 px-16 py-2",
        xs: "gap-1 rounded-sm px-2 py-1 text-xs has-[>svg]:pr-3 has-[>svg]:pl-1.5",
        xl: "h-14 rounded-md px-8 has-[>svg]:px-4 text-[18px] leading-[1.5]",
      },
      variant: {
        default: "bg-primary text-primary-foreground shadow-sm hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 ",
        ghost: "hover:bg-accent hover:text-accent-foreground ",
        link: "text-primary underline-offset-4 hover:underline",
        outline: "border border-input bg-background shadow-xs hover:bg-accent hover:text-accent-foreground ",
        secondary: "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80",
        slateblue: "bg-ausd-slateblue text-primary-foreground shadow-sm hover:bg-ausd-slateblue/90",
      },
    },
  },
);

interface ButtonProps extends ComponentProps<"button">, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /** Shows a spinner and disables the button while an action is in flight. */
  isLoading?: boolean;
}

function Button({
  asChild = false,
  children,
  className,
  disabled,
  isLoading = false,
  size,
  variant,
  ...props
}: ButtonProps) {
  const Comp = asChild ? SlotPrimitive.Slot : "button";

  return (
    <Comp
      className={cn(buttonVariants({ size, variant }), className)}
      data-slot="button"
      disabled={disabled || isLoading}
      {...props}
    >
      {asChild ? (
        // Slot requires a single React element child; don't add sibling nodes
        // (e.g. the spinner slot), or React.Children.only throws.
        children
      ) : (
        <>
          {isLoading && <Spinner className="text-current" size="inside-button" />}
          {children}
        </>
      )}
    </Comp>
  );
}

export { Button, type ButtonProps, buttonVariants };
